import {
  createApi,
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import customFetchBase from '@/app/common/utils/customFetchBase';
import { getIndexerApiBase } from './config';
import type { ApiEnvelope } from './types';
import type {
  EnqueueData,
  EnqueueInspectData,
  HealthData,
  IndexNowKeyData,
  IndexNowSubmitData,
  IndexScheduleItem,
  InspectScheduleItem,
  ScheduleListData,
  ScheduleSaveData,
  InspectTimelineData,
  InspectJobDiffData,
  JobRetryUrlsData,
  NotificationType,
  Project,
  ProjectJob,
  ProjectJobKind,
  ProjectResult,
  RecentIndexData,
  ScheduleMessenger,
  SitemapData,
  UrlHeadCheckData,
  UrlHeadCheckRunData,
  UrlInventoryListData,
  UrlActivityData,
  UrlSitemapSyncData,
  UrlListStatusFilter,
  UrlStatusChangeListData,
  UrlChangeFilter,
  ProjectOverviewData,
  ProjectInspectStatsData,
  ProjectAdvancedStatsData,
  OnboardingStatusData,
  GoogleCredentialsStatusData,
  VerifyAccessData,
  ServiceAccountKeyJson,
} from './types';

type EnvelopeError = FetchBaseQueryError & {
  data?: ApiEnvelope<unknown> | unknown;
};

const rawBaseQuery = (
  args: string | FetchArgs,
  api: Parameters<BaseQueryFn>[1],
  extra: Parameters<BaseQueryFn>[2],
) => customFetchBase(getIndexerApiBase(), args, api, extra);

/** Unwrap `{ code, msg, data }` envelopes from Indexer Nest API. */
const indexerBaseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  EnvelopeError
> = async (args, api, extra) => {
  const result = await rawBaseQuery(args, api, extra);
  if (result.error) {
    return { error: result.error as EnvelopeError };
  }

  const envelope = result.data as ApiEnvelope<unknown> | null | undefined;
  if (
    !envelope ||
    typeof envelope !== 'object' ||
    !('code' in envelope) ||
    !('data' in envelope)
  ) {
    return { data: result.data };
  }

  const code = typeof envelope.code === 'number' ? envelope.code : 200;
  if (code >= 400 || envelope.data === null || envelope.data === undefined) {
    return {
      error: {
        status: code >= 400 ? code : 500,
        data: envelope,
      },
    };
  }

  return { data: envelope.data };
};

const indexerApi = createApi({
  reducerPath: 'indexerApi',
  baseQuery: indexerBaseQuery,
  tagTypes: [
    'Project',
    'Onboarding',
    'GoogleCredentials',
    'Jobs',
    'Job',
    'SchedulesIndex',
    'SchedulesInspect',
    'Urls',
    'UrlChanges',
    'Stats',
    'Health',
    'ServiceAccount',
  ],
  endpoints: build => ({
    getHealth: build.query<HealthData, void>({
      query: () => '/health',
      providesTags: ['Health'],
    }),

    getProject: build.query<Project, string>({
      query: projectId => `/projects/${projectId}`,
      providesTags: (_r, _e, projectId) => [{ type: 'Project', id: projectId }],
    }),

    getProjectBySlug: build.query<Project, string>({
      query: slug => `/projects/by-slug/${encodeURIComponent(slug)}`,
      providesTags: (_r, _e, slug) => [{ type: 'Project', id: `slug:${slug}` }],
    }),

    createProject: build.mutation<
      Project,
      {
        name: string;
        slug: string;
        siteUrl?: string;
        sitemapUrl?: string;
        indexNowDomain?: string;
      }
    >({
      query: body => ({
        url: '/projects',
        method: 'POST',
        body,
      }),
      // عمداً invalidate نمی‌کنیم تا قبل از PATCH onboarding،
      // getProjectBySlug پروژه را زودتر mount نکند و intro قطع نشود.
    }),

    getGoogleServiceAccount: build.query<
      { email: string | null; configured: boolean },
      void
    >({
      query: () => '/google/service-account',
      providesTags: ['ServiceAccount'],
    }),

    getProjectGoogleCredentials: build.query<
      GoogleCredentialsStatusData,
      string
    >({
      query: projectId => `/projects/${projectId}/google-credentials`,
      providesTags: (_r, _e, projectId) => [
        { type: 'GoogleCredentials', id: projectId },
      ],
    }),

    uploadProjectGoogleCredentials: build.mutation<
      { clientEmail: string; uploadedAt: string; configured: boolean },
      { projectId: string; key: ServiceAccountKeyJson }
    >({
      query: ({ projectId, key }) => ({
        url: `/projects/${projectId}/google-credentials`,
        method: 'POST',
        body: key,
      }),
      invalidatesTags: (_r, _e, { projectId }) => [
        { type: 'GoogleCredentials', id: projectId },
      ],
    }),

    deleteProjectGoogleCredentials: build.mutation<
      { configured: boolean },
      string
    >({
      query: projectId => ({
        url: `/projects/${projectId}/google-credentials`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, projectId) => [
        { type: 'GoogleCredentials', id: projectId },
      ],
    }),

    getOnboardingStatus: build.query<OnboardingStatusData, string>({
      query: projectId => `/projects/${projectId}/onboarding`,
      providesTags: (_r, _e, projectId) => [
        { type: 'Onboarding', id: projectId },
      ],
    }),

    patchOnboarding: build.mutation<
      OnboardingStatusData,
      {
        projectId: string;
        step?: number;
        complete?: boolean;
        inspectJobId?: string;
        indexJobId?: string;
        scheduleSkipped?: boolean;
      }
    >({
      query: ({ projectId, ...body }) => ({
        url: `/projects/${projectId}/onboarding`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_r, _e, { projectId }) => [
        { type: 'Onboarding', id: projectId },
      ],
    }),

    verifyOnboardingAccess: build.mutation<VerifyAccessData, string>({
      query: projectId => ({
        url: `/projects/${projectId}/onboarding/verify-access`,
        method: 'POST',
        body: {},
      }),
      invalidatesTags: (_r, _e, projectId) => [
        { type: 'Onboarding', id: projectId },
      ],
    }),

    listProjectResults: build.query<
      ProjectResult[],
      { projectId: string; kind?: ProjectJobKind }
    >({
      query: ({ projectId, kind }) => {
        const q = kind ? `?kind=${encodeURIComponent(kind)}` : '';
        return `/projects/${projectId}/results${q}`;
      },
    }),

    listProjectJobs: build.query<
      ProjectJob[],
      {
        projectId: string;
        kind?: ProjectJobKind | ProjectJobKind[];
        since?: string;
      }
    >({
      query: ({ projectId, kind, since }) => {
        const params = new URLSearchParams();
        if (kind) {
          const kinds = Array.isArray(kind) ? kind : [kind];
          for (const item of kinds) params.append('kind', item);
        }
        if (since) params.set('since', since);
        const q = params.toString() ? `?${params.toString()}` : '';
        return `/projects/${projectId}/jobs${q}`;
      },
      providesTags: (_r, _e, { projectId }) => [
        { type: 'Jobs', id: projectId },
      ],
    }),

    getProjectJob: build.query<
      ProjectJob,
      { projectId: string; jobId: string }
    >({
      query: ({ projectId, jobId }) =>
        `/projects/${projectId}/jobs/${encodeURIComponent(jobId)}`,
      providesTags: (_r, _e, { jobId }) => [{ type: 'Job', id: jobId }],
    }),

    getJobRetryUrls: build.query<
      JobRetryUrlsData,
      { projectId: string; jobId: string }
    >({
      query: ({ projectId, jobId }) =>
        `/projects/${projectId}/jobs/${encodeURIComponent(jobId)}/retry-urls`,
    }),

    cancelProjectJob: build.mutation<
      ProjectJob,
      { projectId: string; jobId: string }
    >({
      query: ({ projectId, jobId }) => ({
        url: `/projects/${projectId}/jobs/${encodeURIComponent(jobId)}/cancel`,
        method: 'POST',
        body: {},
      }),
      invalidatesTags: (_r, _e, { projectId, jobId }) => [
        { type: 'Jobs', id: projectId },
        { type: 'Job', id: jobId },
      ],
    }),

    getInspectJobDiff: build.query<
      InspectJobDiffData,
      { projectId: string; jobId: string; compareTo?: string }
    >({
      query: ({ projectId, jobId, compareTo }) => {
        const q = compareTo
          ? `?compareTo=${encodeURIComponent(compareTo)}`
          : '';
        return `/projects/${projectId}/jobs/${encodeURIComponent(jobId)}/diff${q}`;
      },
    }),

    enqueueIndex: build.mutation<
      EnqueueData,
      { projectId: string; urls: string[]; type: NotificationType }
    >({
      query: ({ projectId, urls, type }) => ({
        url: `/projects/${projectId}/index`,
        method: 'POST',
        body: { urls, type },
      }),
      invalidatesTags: (_r, _e, { projectId }) => [
        { type: 'Jobs', id: projectId },
        { type: 'Stats', id: projectId },
      ],
    }),

    checkRecentIndexUrls: build.mutation<
      RecentIndexData,
      { projectId: string; urls: string[] }
    >({
      query: ({ projectId, urls }) => ({
        url: `/projects/${projectId}/index/recent`,
        method: 'POST',
        body: { urls },
      }),
    }),

    inspectUrls: build.mutation<
      EnqueueInspectData,
      {
        projectId: string;
        urls: string[];
        lastmods?: Record<string, string>;
        retryOf?: string;
      }
    >({
      query: ({ projectId, urls, lastmods, retryOf }) => {
        const body: {
          urls: string[];
          lastmods?: Record<string, string>;
          retryOf?: string;
        } = { urls };
        if (lastmods) body.lastmods = lastmods;
        if (retryOf) body.retryOf = retryOf;
        return {
          url: `/projects/${projectId}/url-inspection`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: (_r, _e, { projectId }) => [
        { type: 'Jobs', id: projectId },
        { type: 'Stats', id: projectId },
      ],
    }),

    getInspectTimeline: build.query<
      InspectTimelineData,
      { projectId: string; url: string }
    >({
      query: ({ projectId, url }) =>
        `/projects/${projectId}/url-inspection/timeline?url=${encodeURIComponent(url)}`,
    }),

    fetchSitemapUrls: build.mutation<
      SitemapData,
      { projectId: string; sitemapUrl?: string }
    >({
      query: ({ projectId, sitemapUrl }) => ({
        url: `/projects/${projectId}/sitemap/urls`,
        method: 'POST',
        body: sitemapUrl ? { sitemapUrl } : {},
      }),
    }),

    checkUrlsHead: build.mutation<
      UrlHeadCheckData,
      { projectId: string; urls: string[] }
    >({
      query: ({ projectId, urls }) => ({
        url: `/projects/${projectId}/urls/head-check`,
        method: 'POST',
        body: { urls },
      }),
    }),

    startHeadCheckRun: build.mutation<
      UrlHeadCheckRunData,
      { projectId: string; urls: string[] }
    >({
      query: ({ projectId, urls }) => ({
        url: `/projects/${projectId}/urls/head-check/run`,
        method: 'POST',
        body: { urls },
      }),
    }),

    generateIndexNowKey: build.mutation<
      IndexNowKeyData,
      { projectId: string; domain?: string }
    >({
      query: ({ projectId, domain }) => ({
        url: `/projects/${projectId}/indexnow/key`,
        method: 'POST',
        body: domain ? { domain } : {},
      }),
      invalidatesTags: (_r, _e, { projectId }) => [
        { type: 'Project', id: projectId },
      ],
    }),

    testIndexNowKey: build.mutation<IndexNowSubmitData, string>({
      query: projectId => ({
        url: `/projects/${projectId}/indexnow/test`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, projectId) => [
        { type: 'Jobs', id: projectId },
      ],
    }),

    submitIndexNow: build.mutation<
      IndexNowSubmitData,
      { projectId: string; urls: string[] }
    >({
      query: ({ projectId, urls }) => ({
        url: `/projects/${projectId}/indexnow/submit`,
        method: 'POST',
        body: { urls },
      }),
      invalidatesTags: (_r, _e, { projectId }) => [
        { type: 'Jobs', id: projectId },
      ],
    }),

    listIndexSchedules: build.query<
      ScheduleListData<IndexScheduleItem>,
      string
    >({
      query: projectId => `/projects/${projectId}/schedules/index`,
      providesTags: (_r, _e, projectId) => [
        { type: 'SchedulesIndex', id: projectId },
      ],
    }),

    createIndexSchedule: build.mutation<
      ScheduleSaveData<IndexScheduleItem>,
      {
        projectId: string;
        name: string;
        enabled?: boolean;
        sitemapUrls?: string[];
        webhookEvents?: string[];
        messengers?: ScheduleMessenger[];
      }
    >({
      query: ({ projectId, ...body }) => ({
        url: `/projects/${projectId}/schedules/index`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { projectId }) => [
        { type: 'SchedulesIndex', id: projectId },
      ],
    }),

    patchIndexSchedule: build.mutation<
      ScheduleSaveData<IndexScheduleItem>,
      {
        projectId: string;
        scheduleId: string;
        name?: string;
        enabled?: boolean;
        sitemapUrls?: string[];
        webhookEvents?: string[];
        messengers?: ScheduleMessenger[];
      }
    >({
      query: ({ projectId, scheduleId, ...body }) => ({
        url: `/projects/${projectId}/schedules/index/${encodeURIComponent(scheduleId)}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_r, _e, { projectId }) => [
        { type: 'SchedulesIndex', id: projectId },
      ],
    }),

    deleteIndexSchedule: build.mutation<
      { deleted: boolean; id: string },
      { projectId: string; scheduleId: string }
    >({
      query: ({ projectId, scheduleId }) => ({
        url: `/projects/${projectId}/schedules/index/${encodeURIComponent(scheduleId)}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, { projectId }) => [
        { type: 'SchedulesIndex', id: projectId },
      ],
    }),

    listInspectSchedules: build.query<
      ScheduleListData<InspectScheduleItem>,
      string
    >({
      query: projectId => `/projects/${projectId}/schedules/url-inspection`,
      providesTags: (_r, _e, projectId) => [
        { type: 'SchedulesInspect', id: projectId },
      ],
    }),

    createInspectSchedule: build.mutation<
      ScheduleSaveData<InspectScheduleItem>,
      {
        projectId: string;
        name: string;
        enabled?: boolean;
        urls?: string[];
        webhookFilters?: string[];
        webhookEvents?: string[];
        autoIndexNotIndexed?: boolean;
        settingsMode?: 'default' | 'advanced';
        messengers?: ScheduleMessenger[];
      }
    >({
      query: ({ projectId, ...body }) => ({
        url: `/projects/${projectId}/schedules/url-inspection`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { projectId }) => [
        { type: 'SchedulesInspect', id: projectId },
      ],
    }),

    patchInspectSchedule: build.mutation<
      ScheduleSaveData<InspectScheduleItem>,
      {
        projectId: string;
        scheduleId: string;
        name?: string;
        enabled?: boolean;
        urls?: string[];
        webhookFilters?: string[];
        webhookEvents?: string[];
        autoIndexNotIndexed?: boolean;
        settingsMode?: 'default' | 'advanced';
        messengers?: ScheduleMessenger[];
      }
    >({
      query: ({ projectId, scheduleId, ...body }) => ({
        url: `/projects/${projectId}/schedules/url-inspection/${encodeURIComponent(scheduleId)}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_r, _e, { projectId }) => [
        { type: 'SchedulesInspect', id: projectId },
      ],
    }),

    deleteInspectSchedule: build.mutation<
      { deleted: boolean; id: string },
      { projectId: string; scheduleId: string }
    >({
      query: ({ projectId, scheduleId }) => ({
        url: `/projects/${projectId}/schedules/url-inspection/${encodeURIComponent(scheduleId)}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, { projectId }) => [
        { type: 'SchedulesInspect', id: projectId },
      ],
    }),

    listProjectUrls: build.query<
      UrlInventoryListData,
      {
        projectId: string;
        q?: string;
        status?: UrlListStatusFilter;
        coverage?: string;
        days?: 1 | 7 | 14 | 30;
        sort?: string;
        order?: 'asc' | 'desc';
        page?: number;
        pageSize?: number;
      }
    >({
      query: ({ projectId, ...params }) => {
        const search = new URLSearchParams();
        if (params.q) search.set('q', params.q);
        if (params.status) search.set('status', params.status);
        if (params.coverage) search.set('coverage', params.coverage);
        if (params.days) search.set('days', String(params.days));
        if (params.sort) search.set('sort', params.sort);
        if (params.order) search.set('order', params.order);
        if (params.page) search.set('page', String(params.page));
        if (params.pageSize) search.set('pageSize', String(params.pageSize));
        const q = search.toString() ? `?${search.toString()}` : '';
        return `/projects/${projectId}/urls${q}`;
      },
      providesTags: (_r, _e, { projectId }) => [
        { type: 'Urls', id: projectId },
      ],
    }),

    syncProjectUrlsFromSitemap: build.mutation<UrlSitemapSyncData, string>({
      query: projectId => ({
        url: `/projects/${projectId}/urls/sync-sitemap`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, projectId) => [{ type: 'Urls', id: projectId }],
    }),

    getUrlActivity: build.query<
      UrlActivityData,
      { projectId: string; url: string; page?: number; pageSize?: number }
    >({
      query: ({ projectId, url, page = 1, pageSize = 20 }) => {
        const params = new URLSearchParams({
          url,
          page: String(page),
          pageSize: String(pageSize),
        });
        return `/projects/${projectId}/urls/activity?${params.toString()}`;
      },
    }),

    listUrlStatusChanges: build.query<
      UrlStatusChangeListData,
      {
        projectId: string;
        filter?: UrlChangeFilter;
        q?: string;
        days?: 1 | 7 | 14 | 30;
        page?: number;
        pageSize?: number;
      }
    >({
      query: ({ projectId, ...params }) => {
        const search = new URLSearchParams();
        if (params.filter) search.set('filter', params.filter);
        if (params.q) search.set('q', params.q);
        if (params.days) search.set('days', String(params.days));
        if (params.page) search.set('page', String(params.page));
        if (params.pageSize) search.set('pageSize', String(params.pageSize));
        const q = search.toString() ? `?${search.toString()}` : '';
        return `/projects/${projectId}/url-changes${q}`;
      },
      providesTags: (_r, _e, { projectId }) => [
        { type: 'UrlChanges', id: projectId },
      ],
    }),

    getProjectOverview: build.query<
      ProjectOverviewData,
      { projectId: string; days?: 1 | 7 | 14 | 30 }
    >({
      query: ({ projectId, days = 7 }) =>
        `/projects/${projectId}/stats/overview?days=${days}`,
      providesTags: (_r, _e, { projectId }) => [
        { type: 'Stats', id: projectId },
      ],
    }),

    getProjectInspectStats: build.query<ProjectInspectStatsData, string>({
      query: projectId => `/projects/${projectId}/stats/inspect`,
      providesTags: (_r, _e, projectId) => [{ type: 'Stats', id: projectId }],
    }),

    getProjectAdvancedStats: build.query<
      ProjectAdvancedStatsData,
      { projectId: string; days?: 1 | 7 | 14 | 30 }
    >({
      query: ({ projectId, days = 7 }) =>
        `/projects/${projectId}/stats/advanced?days=${days}`,
      providesTags: (_r, _e, { projectId }) => [
        { type: 'Stats', id: projectId },
      ],
    }),
  }),
});

export const {
  useGetHealthQuery,
  useGetProjectQuery,
  useGetProjectBySlugQuery,
  useLazyGetProjectBySlugQuery,
  useCreateProjectMutation,
  useGetGoogleServiceAccountQuery,
  useLazyGetGoogleServiceAccountQuery,
  useGetProjectGoogleCredentialsQuery,
  useLazyGetProjectGoogleCredentialsQuery,
  useUploadProjectGoogleCredentialsMutation,
  useDeleteProjectGoogleCredentialsMutation,
  useGetOnboardingStatusQuery,
  useLazyGetOnboardingStatusQuery,
  usePatchOnboardingMutation,
  useVerifyOnboardingAccessMutation,
  useListProjectResultsQuery,
  useListProjectJobsQuery,
  useLazyListProjectJobsQuery,
  useGetProjectJobQuery,
  useLazyGetProjectJobQuery,
  useLazyGetJobRetryUrlsQuery,
  useCancelProjectJobMutation,
  useLazyGetInspectJobDiffQuery,
  useEnqueueIndexMutation,
  useCheckRecentIndexUrlsMutation,
  useInspectUrlsMutation,
  useLazyGetInspectTimelineQuery,
  useFetchSitemapUrlsMutation,
  useCheckUrlsHeadMutation,
  useStartHeadCheckRunMutation,
  useGenerateIndexNowKeyMutation,
  useTestIndexNowKeyMutation,
  useSubmitIndexNowMutation,
  useListIndexSchedulesQuery,
  useLazyListIndexSchedulesQuery,
  useCreateIndexScheduleMutation,
  usePatchIndexScheduleMutation,
  useDeleteIndexScheduleMutation,
  useListInspectSchedulesQuery,
  useLazyListInspectSchedulesQuery,
  useCreateInspectScheduleMutation,
  usePatchInspectScheduleMutation,
  useDeleteInspectScheduleMutation,
  useListProjectUrlsQuery,
  useLazyListProjectUrlsQuery,
  useSyncProjectUrlsFromSitemapMutation,
  useLazyGetUrlActivityQuery,
  useListUrlStatusChangesQuery,
  useLazyListUrlStatusChangesQuery,
  useGetProjectOverviewQuery,
  useLazyGetProjectOverviewQuery,
  useGetProjectInspectStatsQuery,
  useLazyGetProjectInspectStatsQuery,
  useGetProjectAdvancedStatsQuery,
  useLazyGetProjectAdvancedStatsQuery,
} = indexerApi;

export default indexerApi;
